/**
 * Lightbox
 * --------------------------------------------------
 *
 * GLightbox 3.x integration
 *
 * Features:
 * - Uses existing [data-lightbox] links
 * - Groups images inside <main> into one gallery
 * - Treats images outside <main> as individual galleries
 * - Preserves editorial alt text (including empty alt)
 * - Supports optional data-description captions
 * - Enables keyboard and touch navigation
 *
 * Dependencies:
 * - GLightbox 3.x (loaded globally)
 */

class Lightbox {

    constructor() {
        this.dom = {};

        this.dom.win = window;
        this.dom.main = document.querySelector('main');
        this.dom.links = document.querySelectorAll('a[data-lightbox]');

        this.instance = null;

        this.lastTrigger = null;
    }

    /**
     * Prepare gallery groups and image metadata
     */
    prepareElements = () => {

        this.dom.links.forEach((link, index) => {

            // Respect explicitly defined gallery groups
            if (!link.hasAttribute('data-gallery')) {
                link.dataset.gallery = this.dom.main?.contains(link)
                    ? 'main-gallery'
                    : `single-image-${index}`;
            }

            // Preserve editorial alt text, including alt=""
            const image = link.querySelector('img');

            if (image && !link.hasAttribute('data-alt')) {
                link.dataset.alt = image.getAttribute('alt') ?? '';
            }

        });

    }

    /**
     * Features on Open:
     * Set accessability features (custom)
     */
    setDialogAttributes = () => {
        const dialog = document.querySelector('#glightbox-body');

        if (!dialog) return;

        const lang = document.documentElement.lang.toLowerCase();

        const isGerman = lang.startsWith('de');

        const labels = {
            '.gprev': isGerman ? 'Vorheriges Bild' : 'Previous image',
            '.gnext': isGerman ? 'Nächstes Bild' : 'Next image',
            '.gclose': isGerman ? 'Bildansicht schließen' : 'Close image viewer'
        };

        dialog.setAttribute('aria-modal', 'true');
        dialog.setAttribute(
            'aria-label',
            lang.startsWith('de') ? 'Bildergalerie' : 'Image gallery'
        );

        Object.entries(labels).forEach(([selector, label]) => {
            dialog.querySelector(selector)?.setAttribute('aria-label', label);
        });

        // Prevent Console Warnings due to aria-hidden="true" which is set by glightbox
        dialog.focus({ preventScroll: true });
    }

    /*
    * Set Inert
    * Additonally secure all background elements for screenreaders
    */
    setBackgroundInert = (inert) => {
        document.querySelectorAll('body > header, body > main, body > footer')
            .forEach(el => {
                el.inert = inert;
            });
    }

    /**
     * Initialize GLightbox
     */
    createLightbox = () => {

        if (typeof this.dom.win.GLightbox !== 'function') {
            console.warn('Lightbox: GLightbox is not available.');
            return;
        }

        // Respect reduced-motion preference
        const reducedMotion = this.dom.win.matchMedia(
            '(prefers-reduced-motion: reduce)'
        ).matches;

        this.instance = this.dom.win.GLightbox({
            selector: 'a[data-lightbox]',
            touchNavigation: true,
            keyboardNavigation: true,
            closeOnOutsideClick: true,
            loop: false,
            zoomable: false,
            draggable: false,
            
             openEffect: reducedMotion ? 'none' : 'fade',
            closeEffect: reducedMotion ? 'none' : 'fade',
            slideEffect: reducedMotion ? 'none' : 'fade'
        });

        // Features on Open: (Screen Readers)
        this.instance.on('open', () => {
            // setAttr
            this.setDialogAttributes();
            // setBackground Inert 
            this.setBackgroundInert(true);
        });

        // FOCUS on Close: 
        this.instance.on('close', () => {
            // unsetBackground Inert 
            this.setBackgroundInert(false);
            // keep focus on initiating image after using close btn (note: ESC works out of the box)
            if (this.lastTrigger?.isConnected) {
                this.lastTrigger.focus({ preventScroll: true });
            }
        });
    }

    /**
     * Register additional event listeners
     *
     * Reserved for accessibility enhancements:
     * - Focus management
     * - Focus restoration
     * - Dialog semantics
     */
    addListeners() {

        // No additional listeners required yet.
        // GLightbox handles click, keyboard and touch events.

        // FOCUS on Close: 
        // listen to links so we can set the lastTrigger in order to retain foucs on close
        this.dom.links.forEach(link => {
            link.addEventListener('click', () => {
                this.lastTrigger = link;
            });
        });
    }

    /**
     * Initialize module
     */
    init() {

        if (!this.dom.links.length) {
            return;
        }

        this.prepareElements();
        this.createLightbox();
        this.addListeners();

    }

}

export default Lightbox;




/**
 * GLightbox initialization
 * --------------------------------------------------
 *
 * - Uses existing [data-lightbox] links
 * - Groups all images inside <main> into one gallery
 * - Keeps images outside <main> as individual lightboxes
 * - Transfers image alt text to GLightbox
 * - Supports optional data-description captions
 * - Enables keyboard and touch navigation
 *
 * Requires GLightbox 3.x
 */

/*
// NON ES-6
document.addEventListener('DOMContentLoaded', () => {

    if (typeof GLightbox === 'undefined') {
        return;
    }

    const links = document.querySelectorAll('a[data-lightbox]');

    if (!links.length) {
        return;
    }

    links.forEach((link, index) => {

        // Group all images inside <main>
        // Outside <main>, each image gets its own gallery
        const gallery = link.closest('main')
            ? 'main-gallery'
            : `single-image-${index}`;

        link.dataset.gallery = gallery;

        // Preserve editorial alt text, including intentionally empty alt
        const image = link.querySelector('img');

        if (image && !link.hasAttribute('data-alt')) {
            link.dataset.alt = image.getAttribute('alt') ?? '';
        }
    });

    GLightbox({
        selector: 'a[data-lightbox]',
        touchNavigation: true,
        keyboardNavigation: true,
        closeOnOutsideClick: true,
        loop: false,
        zoomable: false,
        draggable: false,
        openEffect: 'fade',
        closeEffect: 'fade',
        slideEffect: 'fade'
    });

});

// Non-ES-6 EOF
*/
